/**
 * Modification du point (PATCH /api/tickets/:id, agents Digital Team) : corps
 * de la requête limité aux champs réellement changés. L'API n'écrit de toute
 * façon que les colonnes qui changent ; n'envoyer que la différence évite
 * d'écraser un champ modifié entre-temps dans le V1 par une valeur affichée
 * périmée (un formulaire V1, lui, réécrit tout).
 */
export const TICKET_TYPES = ['Bug', 'Evolution', 'Question', 'DP'] as const;
export type TicketType = (typeof TICKET_TYPES)[number];

export interface TicketEditValues {
  nom_tache: string;
  type_tache: TicketType;
  priorite: number;
  id_user: number;
  link_notifier: number[];
}

export type TicketEditBody = Partial<TicketEditValues> & { changer_client?: true };

/**
 * Changement de client par l'auteur (auteur d'un AUTRE client) : confirmation
 * explicite pour l'API, et pas de notifiés (l'API vide ceux de l'ancien client).
 */
export function withClientMove(body: TicketEditBody): TicketEditBody {
  const rest: TicketEditBody = { ...body };
  delete rest.link_notifier;
  return { ...rest, changer_client: true };
}

/** Texte de confirmation d'un changement de client (effets annoncés). */
export function clientMoveWarning(from: string, to: string): string {
  return (
    `Le point passera du client « ${from} » au client « ${to} ».\n\n` +
    `- « ${to} » le verra avec tout son historique public ; « ${from} » ne le verra plus.\n` +
    `- Les notifiés actuels seront retirés.\n` +
    `- Traitants : s'ils sont ceux par défaut de « ${from} », ils deviennent ceux de « ${to} » ; choisis à la main, ils sont conservés.\n` +
    `- Refusé si du temps a déjà été saisi ou décompté sur ce point (recréer le point dans ce cas).\n\n` +
    `Continuer ?`
  );
}

const sameIds = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/** Liste sans doublon, dans l'ordre reçu (même normalisation que l'API). */
export function uniqueIds(ids: number[]): number[] {
  return [...new Set(ids)];
}

export function ticketEditBody(initial: TicketEditValues, next: TicketEditValues): TicketEditBody {
  const body: TicketEditBody = {};
  const title = next.nom_tache.trim();
  if (title !== initial.nom_tache.trim()) body.nom_tache = title;
  if (next.type_tache !== initial.type_tache) body.type_tache = next.type_tache;
  if (next.priorite !== initial.priorite) body.priorite = next.priorite;
  if (next.id_user !== initial.id_user) body.id_user = next.id_user;
  const notif = uniqueIds(next.link_notifier);
  if (!sameIds(notif, uniqueIds(initial.link_notifier))) body.link_notifier = notif;
  return body;
}

/**
 * Notifiés proposés : utilisateurs du client du point sauf l'auteur, comme la
 * liste du V1 (combo_notif_multiple), plus les notifiés déjà enregistrés hors
 * de cette liste (Digital Team, ou l'auteur) pour qu'ils ne disparaissent pas
 * sans qu'on les ait décochés.
 */
export function notifierChoices(
  clientUsers: { id_user: number; nom_user: string }[],
  authorId: number,
  current: number[],
  nameOf: (id: number) => string | undefined,
): { id_user: number; nom_user: string }[] {
  const list = clientUsers.filter((u) => u.id_user !== authorId);
  const known = new Set(list.map((u) => u.id_user));
  for (const id of uniqueIds(current)) {
    if (!known.has(id)) list.push({ id_user: id, nom_user: nameOf(id) ?? `Utilisateur #${id}` });
  }
  return list;
}

/**
 * Notifiés envoyés à la création (POST /api/tickets, agents Digital Team) :
 * seulement ceux encore proposés (le client ou l'auteur a pu changer depuis
 * qu'on les a cochés), sans doublon ; `undefined` si aucun, pour ne pas
 * envoyer le champ.
 */
export function creationNotifiers(
  selected: number[],
  choices: { id_user: number }[],
): number[] | undefined {
  const offered = new Set(choices.map((c) => c.id_user));
  const kept = uniqueIds(selected).filter((id) => offered.has(id));
  return kept.length > 0 ? kept : undefined;
}
